import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { catchError, finalize, forkJoin } from "rxjs";
import { ToastrService } from "ngx-toastr";

// NG-ZORRO Imports
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from "ng-zorro-antd/select";
import { NzTagModule } from "ng-zorro-antd/tag";
import { NzSpaceModule } from "ng-zorro-antd/space";
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzTooltipModule } from "ng-zorro-antd/tooltip";
import { NzUploadModule } from 'ng-zorro-antd/upload';

import { GoogleFileDto, PermissionEnum, SdkAssetDto, SdkAssetTypeEnum, SdkCategoryDto, SdkDto, SdkPlatformEnum, SdkVendorDto } from "../../../dto";
import { HttpSdkService } from "../../../services/sdk/http-sdk.service";
import { DriveFilePickerModalComponent } from "../../../components/drive-file-picker/drive-file-picker-modal.component";
import { DriveFileCardComponent } from "../../../components/drive-file-card/drive-file-card.component";
import { UnauthorizedAccessEditWarningComponent } from "../../../components/unauthorized-access-edit-warning/unauthorized-access-edit-warning.component";
import { AuthService } from "../../../services/auth/auth.service";

@Component({
    selector: 'app-sdk-editor-modal',
    standalone: true,
    imports: [
        CommonModule, 
        ReactiveFormsModule,
        NzModalModule,
        NzButtonModule,
        NzFormModule,
        NzInputModule,
        NzUploadModule,
        NzSelectModule,
        NzTagModule,
        NzSpaceModule,
        NzIconModule,
        DriveFilePickerModalComponent,
        UnauthorizedAccessEditWarningComponent,
        DriveFileCardComponent,
        NzTooltipModule
    ],
    templateUrl: './sdk-editor-modal.component.html',
    styleUrls: ['./sdk-editor-modal.component.scss']
})
export class SdkEditorModalComponent implements OnInit {
    @Input() modalId = 'sdkEditorModal';
    @Output() saved = new EventEmitter<SdkDto>();
    @Output() cancelled = new EventEmitter<void>();

    editingSdkId?: number; 
    modalTitle = 'Add SDK';

    form!: FormGroup;
    
    isVisible = false;
    isConfirmLoading = false;

    categories?: SdkCategoryDto[];
    vendors?: SdkVendorDto[];

    platforms = Object.values(SdkPlatformEnum);
    platform = SdkPlatformEnum;

    assetType = SdkAssetTypeEnum;
    currentPickerTarget: SdkAssetTypeEnum = SdkAssetTypeEnum.Src;
    currentPickerDocIndex?: number;
    
    // UX Tracking: Remember the last folder the user was in
    lastAccessedFolderId?: string;

    @ViewChild(DriveFilePickerModalComponent) drivePicker!: DriveFilePickerModalComponent;

    constructor(
        private fb: FormBuilder,
        private toastr: ToastrService,
        private httpService: HttpSdkService,
        private authService: AuthService
    ) { }

    get canEdit(): boolean {
        return this.authService.hasAccess(PermissionEnum.SdksEdit);
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            category: [null, Validators.required],
            vendor: [null, Validators.required],
            name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
            description: ['', [Validators.minLength(2), Validators.maxLength(2000)]],
            platforms: [[], Validators.required],
            srcFiles: this.fb.array([]),
            libFiles: this.fb.array([]),
            demoFiles: this.fb.array([]),
            documentationFiles: this.fb.array([])
        });

        this.fetchData();
    }

    // Helpers to access the FormArrays easily in the template
    get srcControls() { return this.form.get('srcFiles') as FormArray; }
    get libControls() { return this.form.get('libFiles') as FormArray; }
    get demoControls() { return this.form.get('demoFiles') as FormArray; }
    get docControls() { return this.form.get('documentationFiles') as FormArray; }

    getControls(target: SdkAssetTypeEnum): FormArray {
        switch(target) {
            case SdkAssetTypeEnum.Src: return this.srcControls;
            case SdkAssetTypeEnum.Lib: return this.libControls;
            case SdkAssetTypeEnum.Demo: return this.demoControls;
            case SdkAssetTypeEnum.Documentation: return this.docControls;
        }
    }

    fetchData() {
        this.isConfirmLoading = true; 
        forkJoin({
            categories: this.httpService.getSdkCategories(),
            vendors: this.httpService.getSdkVendors()
        })
        .pipe(
            catchError(error => {
                console.error('Error fetching SDK reference data', error);
                this.toastr.error('Failed to fetch form data: ' + (error.error?.error || error.message));
                throw error;
            }),
            finalize(() => this.isConfirmLoading = false) 
        )
        .subscribe(data => {
            this.categories = data.categories;
            this.vendors = data.vendors;
        });
    }

    open(sdk?: SdkDto) {
        this.form.reset();
        this.srcControls.clear();
        this.libControls.clear();
        this.demoControls.clear();
        this.docControls.clear();
        
        // Reset UX state for the new session
        this.lastAccessedFolderId = undefined;
        
        if (sdk) {
            this.editingSdkId = sdk.id;
            this.modalTitle = 'Edit SDK';
            this.form.patchValue({
                category: sdk.category,
                vendor: sdk.vendor,
                name: sdk.name,
                description: sdk.description,
                platforms: sdk.platforms || [],
            });

            if (sdk.assets && sdk.assets.length > 0) {
                sdk.assets.forEach(asset => {
                    if (!asset.driveFile) return;

                    const group = this.fb.group({
                        id: [asset.id],
                        driveFileId: [asset.driveFile.driveFileId, Validators.required],
                        driveFolderId: [asset.driveFile.driveFolderId],
                        driveFileName: [asset.driveFile.driveFileName, Validators.required],
                        driveFileLink: [asset.driveFile.driveFileLink, Validators.required]
                    });

                    if (asset.assetType === SdkAssetTypeEnum.Src) {
                        this.srcControls.push(group);
                    } else if (asset.assetType === SdkAssetTypeEnum.Lib) {
                        this.libControls.push(group);
                    } else if (asset.assetType === SdkAssetTypeEnum.Demo) {
                        this.demoControls.push(group);
                    } else if (asset.assetType === SdkAssetTypeEnum.Documentation) {
                        this.docControls.push(group);
                    }
                });
            }
        } else {
            this.editingSdkId = undefined;
            this.modalTitle = 'Add SDK';
        }

        this.isVisible = true;

        if (!this.canEdit) {
            this.form.disable();
        } else {
            this.form.enable();
        }
    }

    handleCancel(): void {
        this.isVisible = false;
        this.cancelled.emit();
    }

    save(): void {
        if (this.form.invalid) {
            Object.values(this.form.controls).forEach(control => {
                if (control.invalid) {
                    control.markAsDirty();
                    control.updateValueAndValidity({ onlySelf: true });
                }
            });
            return;
        }

        this.isConfirmLoading = true; 

        const mergedAssets: SdkAssetDto[] = [];
        
        const mapAssets = (formArray: FormArray, type: SdkAssetTypeEnum) => {
            formArray.controls.forEach(ctrl => {
                mergedAssets.push({
                    id: ctrl.value.id,
                    assetType: type,
                    driveFile: {
                        driveFileId: ctrl.value.driveFileId,
                        driveFolderId: ctrl.value.driveFolderId,
                        driveFileName: ctrl.value.driveFileName,
                        driveFileLink: ctrl.value.driveFileLink
                    }
                });
            });
        };

        mapAssets(this.srcControls, SdkAssetTypeEnum.Src);
        mapAssets(this.libControls, SdkAssetTypeEnum.Lib);
        mapAssets(this.demoControls, SdkAssetTypeEnum.Demo);
        mapAssets(this.docControls, SdkAssetTypeEnum.Documentation);

        const payload: SdkDto = {
            id: this.editingSdkId,
            category: this.form.value.category,
            vendor: this.form.value.vendor,
            name: this.form.value.name,
            description: this.form.value.description,
            platforms: this.form.value.platforms,
            assets: mergedAssets
        };

        this.httpService.saveSdk(payload)
            .pipe(
                catchError(error => {
                    console.error('Error saving SDK', error);
                    this.toastr.error('Failed to save SDK: ' + (error.error?.error || error.message));
                    throw error;
                }),
                finalize(() => this.isConfirmLoading = false) 
            )
            .subscribe(data => {
                if (!data.success) {
                    this.toastr.error('Failed to save SDK: ' + data.error);
                    return;
                }
                this.toastr.success('SDK saved successfully');
                this.saved.emit(payload);
                this.isVisible = false;
            });
    }

    compareFn = (o1: any, o2: any): boolean => (o1 && o2 ? o1.id === o2.id : o1 === o2);

    openDrivePicker(target: SdkAssetTypeEnum, index?: number) {
        this.currentPickerTarget = target;
        this.currentPickerDocIndex = index;

        // Base fallback: the last accessed folder globally in this modal session
        let targetFolderId = this.lastAccessedFolderId;

        // If editing an existing file that already lives in a specific folder, prioritize that folder
        if (index !== undefined) {
            const specificFileFolderId = this.getControls(target).at(index).get('driveFolderId')?.value;
            if (specificFileFolderId) {
                targetFolderId = specificFileFolderId;
            }
        }

        if (targetFolderId) {
            this.drivePicker.open(targetFolderId, target.toString());
        } else {
            this.drivePicker.open(undefined, target.toString());
        }
    }

    onDriveFileSelected(input: { file: GoogleFileDto, folderId?: string }) {
        const file = input.file;
        let link = file.webViewLink;
        if (!link) {
            link = `https://drive.google.com/file/d/${file.id}/view`;
        }
        
        // UX Tracking: Save this folder so the next picker session starts here
        if (input.folderId) {
            this.lastAccessedFolderId = input.folderId;
        }

        const controls = this.getControls(this.currentPickerTarget);

        if (this.currentPickerDocIndex !== undefined) {
            // Replacing an existing file
            controls.at(this.currentPickerDocIndex).patchValue({
                driveFileId: file.id,
                driveFolderId: input.folderId,
                driveFileName: file.name,
                driveFileLink: link
            });
        } else {
            // Adding a brand new file
            controls.push(this.fb.group({
                id: [null],
                driveFileId: [file.id, Validators.required],
                driveFolderId: [input.folderId],
                driveFileName: [file.name, Validators.required],
                driveFileLink: [link, Validators.required]
            }));

            // Auto-fill the SDK name if this is the first Src file and name is empty
            if (this.currentPickerTarget === SdkAssetTypeEnum.Src && !this.form.get('name')?.value) {
                this.form.get('name')?.setValue(file.name);
            }
        }
        this.form.markAsDirty();
    }

    removeAsset(target: SdkAssetTypeEnum, index: number) {
        this.getControls(target).removeAt(index);
        this.form.markAsDirty();
    }
}